import { Chip, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { observer } from 'mobx-react-lite'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { getColorByRating } from '../../util/skillGapUtils'

const RATING_OPTIONS = [1, 2, 3, 4, 5] as const

export type RatingFilterProps = {
  minRating: number | null
  maxRating: number | null
  onRatingClick: (rating: number) => void
  isRatingSelected: (rating: number) => boolean
}

export const RatingFilter = observer(({ minRating, maxRating, onRatingClick, isRatingSelected }: RatingFilterProps) => {
  const { translation } = useLocalizationStore()
  const pageTranslation = translation.pages.myTeam.skillGapInsights

  return (
    <AppBox sx={styles.filterSection}>
      <Caption sx={styles.filterLabel}>
        {pageTranslation.filterByRating()} {minRating !== null && maxRating !== null && `(${minRating}★ - ${maxRating}★)`}
      </Caption>
      <AppBox sx={styles.ratingContainer}>
        {RATING_OPTIONS.map(rating => (
          <Chip
            key={rating}
            label={
              <AppBox sx={styles.chipContent}>
                <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: isRatingSelected(rating) ? 'primary.contrastText' : 'inherit' }}>
                  {rating}
                </Typography>
                <Typography sx={{ ...styles.chipStar, color: isRatingSelected(rating) ? 'primary.contrastText' : 'inherit' }}>
                  ★
                </Typography>
              </AppBox>
            }
            onClick={() => onRatingClick(rating)}
            variant={isRatingSelected(rating) ? 'filled' : 'outlined'}
            color={isRatingSelected(rating) ? 'primary' : 'default'}
            sx={{
              ...styles.chip,
              '&.MuiChip-filled': {
                backgroundColor: getColorByRating(rating),
                color: 'primary.contrastText',
              },
            }}
          />
        ))}
      </AppBox>
    </AppBox>
  )
})

const styles = {
  filterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    width: '100%',
  },
  filterLabel: {
    fontSize: 11,
    color: 'text.secondary',
  },
  ratingContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: { xs: 4, sm: 5 },
    width: '100%',
  },
  chip: {
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    padding: { xs: '3px 2px', sm: '4px 2px' },
    height: 'auto',
    fontSize: { xs: 10, sm: 11 },
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  chipContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  chipStar: {
    fontSize: { xs: 10, sm: 11 },
    lineHeight: 1,
  },
}
